import { Component, Input, Output, EventEmitter, forwardRef, ViewChild, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { AutoComplete } from 'primeng/autocomplete';

import { OnNativeFocus } from 'src/app/shared/utils/form';
import { SearchFunc } from 'src/app/shared/utils/func';


export class SearchItems {
  value: any;
  text: string;
}

export class CategoriesAutocomplete {
  id: number;
  text: string;
  field_name: string;
  icon?: string;
}

export interface FilterAutoComplete {
  autoComplete(query: string): Observable<SearchItems[]>;
}

@Component({
  selector: 'app-auto-complete',
  templateUrl: './auto-complete.component.html',
  styleUrls: ['./auto-complete.component.sass'],
  styles: [':host { display: block; width: 100%; }'],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AutoCompleteComponent), multi: true }]
})
export class AutoCompleteComponent implements ControlValueAccessor, OnNativeFocus, OnInit {
  @ViewChild('autoComplete') autoComplete: AutoComplete;

  @Input() forceSelection = true;
  @Input() id: string;
  @Input() placeholder: string;
  @Input() propLabel: string = null;
  @Input() propValue: string = null;
  @Input() readOnly = false;
  @Input() search: SearchFunc;
  @Input() styleClass = 'form-control';
  @Input() value: any = null;
  @Input() categories: CategoriesAutocomplete[] = [];

  @Output() opened = new EventEmitter();
  @Output() closed = new EventEmitter();
  @Output() selected = new EventEmitter();
  @Output() deselected = new EventEmitter();
  @Output() cleared = new EventEmitter();
  @Output() blur = new EventEmitter();

  public suggestions: any[];
  public timeout: any = null;

  private searchTerms: Subject<string> = new Subject();
  private onChangeCallback: (_: any) => void = () => { };
  private onTouchedCallback: () => void = () => { };

  public ngOnInit() {
    this.searchTerms.pipe(
      debounceTime(300),
      distinctUntilChanged((prev, curr) => {
        const isEqual = prev === curr;
        if (isEqual) {
          this.setItens(this.suggestions);
        }
        return isEqual;
      }),
      switchMap(
        term => term ? this.search(term) : of<any[]>([])
      )
    ).subscribe(items => this.setItens(items), error => this.setItens([]));
  }

  public focus(): void {
    if (this.autoComplete && this.autoComplete.inputEL && this.autoComplete.inputEL.nativeElement) {
      setTimeout(() => {
        this.autoComplete.inputEL.nativeElement.focus();
      }, 0);
    }
  }

  public clear(): void {
    this.clearSearch();
  }

  private clearSearch(): void {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }

    // this.suggestions = null;
    this.value = null;
    this.onChangeCallback(null);
    this.autoComplete?.writeValue(null);
  }

  public completeMethod(event): void {
    this.searchTerms.next(event.query);
  }

  public registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  private setItens(itens: any[]): void {
    if (!itens) {
      this.suggestions = [];
      return;
    }

    const temp = [];

    if (this.value === (typeof this.value === 'object')) {
      if (!this.suggestions.some(item => item.id === this.value.id)) {
        temp.push(this.value);
      }
    }

    this.suggestions = [...temp, ...itens];
  }

  public writeValue(value: any): void {
    if (value instanceof Object || !this.propValue) {
      this.value = value;
    } else if (value) {
      // Backend do service deve conter filtro pela chave primária. Ver módulo People como exemplo: /api/people/person/
      this.search(value).subscribe(res => {
        const new_value = res.find(item => item[this.propValue].toString() === value.toString());
        if (new_value) {
          this.value = {
            [this.propLabel]: new_value[this.propLabel],
            [this.propValue]: new_value[this.propValue],
          };
        }
      }, error => console.log(error));
    }

    if (!value) {
      this.clear();
    } else {
      setTimeout(() => this.onChangeCallback(value), 150);
    }
  }

  public onChange(event): void {
    if (!event) {
      this.onChangeCallback(null);
      return;
    }

    if (!this.propValue) {
      this.onChangeCallback(event);
      return;
    }

    this.onChangeCallback(event[this.propValue]);
  }
}
